/**
 * Preferred case deep-link handling.
 *
 * When a policyholder opens a deep link such as `/:company/cases/:caseId` while
 * not authenticated, the requested target is remembered so that after login the
 * user is taken directly to that case (even when multiple cases are available).
 *
 * The value is stored in localStorage (shared across tabs/windows) so it
 * survives the login redirect and any cross-window verification flows. To limit
 * how long an unauthenticated, attacker-controllable URL can influence a later
 * login, the entry is scoped to the company it was captured for, expires after a
 * short window and is cleared whenever authentication state is cleared.
 */

const PREFERRED_CASE_KEY = 'preferred_case_id'
const DEFAULT_ROUTE_NAME = 'company-case-details'

// How long a deep link may influence a later login. Keeps a stale value from
// hijacking an unrelated login on a shared browser.
const PREFERRED_CASE_TTL_MS = 15 * 60 * 1000

/**
 * Read and validate the stored preferred case entry.
 *
 * Returns `null` (and removes the stored value) when the entry is missing,
 * malformed, legacy/plain or expired. This keeps `localStorage` from being
 * treated as a trusted source.
 *
 * @returns {{caseId: string, company: string, routeName: string, expiresAt: number}|null}
 */
function readPreferredCase() {
  const raw = localStorage.getItem(PREFERRED_CASE_KEY)
  if (!raw) {
    return null
  }

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Legacy plain value or corrupted data: discard it.
    localStorage.removeItem(PREFERRED_CASE_KEY)
    return null
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    typeof parsed.caseId !== 'string' ||
    !parsed.caseId ||
    typeof parsed.company !== 'string' ||
    !parsed.company
  ) {
    localStorage.removeItem(PREFERRED_CASE_KEY)
    return null
  }

  if (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
    localStorage.removeItem(PREFERRED_CASE_KEY)
    return null
  }

  return {
    caseId: parsed.caseId,
    company: parsed.company,
    routeName: resolveRouteName(parsed.routeName),
    expiresAt: parsed.expiresAt,
  }
}

/**
 * Only replay route names that belong to the case flow. Anything unexpected
 * (including the wizard parent) falls back to the case overview.
 *
 * @param {unknown} routeName
 * @returns {string}
 */
function resolveRouteName(routeName) {
  if (typeof routeName === 'string' && routeName && routeName !== 'wizard') {
    return routeName
  }
  return DEFAULT_ROUTE_NAME
}

/**
 * Remember the preferred deep-link target for the current company.
 *
 * @param {string|number|null|undefined} caseId - The case id from the deep link.
 * @param {string|null|undefined} company - The company slug the deep link belongs to.
 * @param {string} [routeName] - The resolved route name to restore after login.
 */
export function setPreferredCaseId(caseId, company, routeName = DEFAULT_ROUTE_NAME) {
  if (caseId === null || caseId === undefined || caseId === '') {
    return
  }
  if (!company) {
    return
  }
  const payload = {
    caseId: String(caseId),
    company: String(company),
    routeName: resolveRouteName(routeName),
    expiresAt: Date.now() + PREFERRED_CASE_TTL_MS,
  }
  localStorage.setItem(PREFERRED_CASE_KEY, JSON.stringify(payload))
}

/**
 * Read the currently remembered preferred case id, if any.
 *
 * @returns {string|null}
 */
export function getPreferredCaseId() {
  const preferred = readPreferredCase()
  return preferred ? preferred.caseId : null
}

/**
 * Clear the remembered preferred case target.
 */
export function clearPreferredCaseId() {
  localStorage.removeItem(PREFERRED_CASE_KEY)
}

/**
 * Build the route to navigate to after a successful login.
 *
 * The deep link is only honoured when it was captured for the same company that
 * is now authenticating. Otherwise (no deep link, different company, expired or
 * malformed value) the user is taken to the case list, preserving the legacy
 * behaviour.
 *
 * @param {string} company - The company slug.
 * @returns {{name: string, params: {company: string, caseId?: string}}}
 */
export function getPostLoginRoute(company) {
  const preferred = readPreferredCase()
  if (preferred && preferred.company === company) {
    clearPreferredCaseId()
    return {
      name: preferred.routeName,
      params: { company, caseId: preferred.caseId },
    }
  }
  // Never replay a deep link for a different company (shared browser/stale value).
  if (preferred) {
    clearPreferredCaseId()
  }
  return { name: 'company-cases', params: { company } }
}
